import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  MagnifyingGlass, WaveSine, Robot, ArrowBendUpRight, Headset, PhoneCall, Broadcast,
  PhoneIncoming, PhoneDisconnect, PhonePlus, Ear, PencilSimple,
} from "@phosphor-icons/react";
import { dialingTypeLabel } from "@/lib/campaignForm";

const REFRESH_MS = 5000;
const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const TABS = [
  { k: "agents", label: "Agents", icon: Headset },
  { k: "inbound", label: "Inbound", icon: PhoneIncoming },
  { k: "predictive", label: "Predictive", icon: Broadcast },
  { k: "manual", label: "Manual", icon: PhoneCall },
  { k: "ai_bot", label: "VICI Bot", icon: Robot },
];

const fmtDur = (s) => {
  if (s === null || s === undefined) return "—";
  const sec = Math.max(0, Math.floor(s));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
};

const liveDuration = (startedAt, fallback, nowMs) => {
  if (!startedAt) return fallback ?? null;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return fallback ?? null;
  return Math.max(0, Math.round((nowMs - t) / 1000));
};

const StatusBadge = ({ row }) => {
  const label = row.state_label || row.call_to || row.state || "Live";
  const key = String(label).toLowerCase();
  const tone =
    key === "incall" ? "bg-red-100 text-red-700"
      : key === "on hold" ? "bg-sky-100 text-sky-800"
      : key === "queue" ? "bg-amber-100 text-amber-800"
        : key === "ringing" || key === "dialing" ? "bg-orange-100 text-orange-700"
          : key === "transferred" ? "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]"
            : key === "call follow" ? "bg-violet-100 text-violet-700"
              : "bg-primary/10 text-primary";
  return (
    <span
      data-testid="live-call-status"
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}
    >
      {label}
    </span>
  );
};

const CallToBadge = ({ state, callTo }) => {
  const transferred = state === "transferred";
  const follow = String(callTo || state || "").toLowerCase().includes("follow");
  const Icon = transferred ? ArrowBendUpRight : Robot;
  const label = transferred ? "Transferred" : follow ? "Call Follow" : "Bot";
  return (
    <span
      data-testid="live-call-callto"
      data-state={transferred ? "transferred" : follow ? "call_follow" : "bot"}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold " +
        (transferred
          ? "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))]"
          : follow
            ? "bg-violet-100 text-violet-700"
            : "bg-primary/10 text-primary")
      }
    >
      <Icon className="w-3.5 h-3.5" weight="bold" />
      {label}
    </span>
  );
};

const matchesQuery = (r, s) => {
  if (!s) return true;
  return [
    r.phone_number, r.callerid, r.client_id, r.client, r.campaign_id,
    r.campaign_name, r.campaign_prefix, r.lead_id, r.vicidial_ip, r.call_to,
    r.agent_name, r.agent_sip, r.customer_name, r.state_label, r.dialing_type,
  ].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
};

const rowKey = (r) => r.call_id || r.session_id || r.call_uuid || "";

const canDisconnect = (r) => Boolean(r && (r.can_disconnect !== false) && rowKey(r));

export default function LiveCalls() {
  const [board, setBoard] = useState({
    tabs: { agents: [], inbound: [], predictive: [], manual: [], ai_bot: [] },
    counts: { agents: 0, inbound: 0, predictive: 0, manual: 0, ai_bot: 0, all: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("agents");
  const [q, setQ] = useState("");
  const [now, setNow] = useState(Date.now());
  const [confirm, setConfirm] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [monitorBusy, setMonitorBusy] = useState("");
  const [monitorSip, setMonitorSip] = useState("");
  const [extOpen, setExtOpen] = useState(false);
  const [extDraft, setExtDraft] = useState("");
  const [extSaving, setExtSaving] = useState(false);
  const firstLoad = useRef(true);

  const loadExtension = useCallback(async () => {
    try {
      const { data } = await api.get("/live-calls/monitoring-extension");
      setMonitorSip(data.sip_username || "");
    } catch {
      setMonitorSip("");
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/live-calls");
      const tabs = data.tabs || {
        agents: [],
        inbound: [],
        predictive: [],
        manual: [],
        ai_bot: data.items || [],
      };
      const counts = data.counts || {
        agents: tabs.agents.length,
        inbound: (tabs.inbound || []).length,
        predictive: tabs.predictive.length,
        manual: tabs.manual.length,
        ai_bot: tabs.ai_bot.length,
        all: data.total ?? (data.items || []).length,
      };
      setBoard({ tabs, counts });
    } catch {
      /* transient polling error — keep last snapshot */
    } finally {
      if (firstLoad.current) { setLoading(false); firstLoad.current = false; }
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadExtension(); }, [loadExtension]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const disconnectCall = useCallback(async (row) => {
    if (!canDisconnect(row)) return;
    const key = rowKey(row);
    setBusyKey(key);
    try {
      await api.post("/live-calls/disconnect", {
        source: row.source || undefined,
        call_id: row.call_id || undefined,
        session_id: row.session_id || undefined,
        call_uuid: row.call_uuid || undefined,
      });
      toast.success("Call disconnected");
      setConfirm(null);
      setBoard((prev) => {
        const nextTabs = {};
        Object.entries(prev.tabs || {}).forEach(([k, list]) => {
          nextTabs[k] = (list || []).filter((r) => rowKey(r) !== key);
        });
        const counts = {
          agents: nextTabs.agents?.length || 0,
          inbound: nextTabs.inbound?.length || 0,
          predictive: nextTabs.predictive?.length || 0,
          manual: nextTabs.manual?.length || 0,
          ai_bot: nextTabs.ai_bot?.length || 0,
        };
        counts.all = Object.values(nextTabs).reduce((n, list) => n + (list?.length || 0), 0);
        return { tabs: nextTabs, counts };
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not disconnect the call");
    } finally {
      setBusyKey("");
    }
  }, [load]);

  const saveExtension = useCallback(async () => {
    const sip = (extDraft || "").trim();
    if (!sip) {
      toast.error("Enter a SIP phone username");
      return;
    }
    setExtSaving(true);
    try {
      const { data } = await api.put("/live-calls/monitoring-extension", { sip_username: sip });
      setMonitorSip(data.sip_username || sip);
      setExtOpen(false);
      toast.success("Monitoring extension saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save monitoring extension");
    } finally {
      setExtSaving(false);
    }
  }, [extDraft]);

  const deleteExtension = useCallback(async () => {
    setExtSaving(true);
    try {
      await api.delete("/live-calls/monitoring-extension");
      setMonitorSip("");
      setExtDraft("");
      setExtOpen(false);
      toast.success("Monitoring extension removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove monitoring extension");
    } finally {
      setExtSaving(false);
    }
  }, []);

  const monitorCall = useCallback(async (row, mode) => {
    const key = `${rowKey(row)}:${mode}`;
    setMonitorBusy(key);
    try {
      await api.post("/live-calls/monitor", {
        source: row.source || undefined,
        call_id: row.call_id || undefined,
        session_id: row.session_id || undefined,
        call_uuid: row.call_uuid || undefined,
        mode,
      });
      toast.success(mode === "silent" ? "Ringing monitor (listen only)" : "Ringing monitor to join the call");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start monitoring");
    } finally {
      setMonitorBusy("");
    }
  }, []);

  const rows = board.tabs[tab] || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = board.tabs[tab] || [];
    return list.filter((r) => matchesQuery(r, s));
  }, [board.tabs, tab, q]);

  const emptyCopy = {
    agents: "No agents are on a call and no customers are waiting in queue.",
    inbound: "No inbound DID calls are waiting or connected right now.",
    predictive: "No Predictive campaigns are dialing right now.",
    manual: "No Manual calls are in progress.",
    ai_bot: "No VICI Bot calls are connected right now.",
  };

  return (
    <div data-testid="live-calls-page">
      <PageHeader
        title="Live Calls"
        subtitle="Agent, Inbound, Predictive, Manual, and AI bot calls across all campaigns — updates every few seconds"
      >
        <span
          className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.1)] px-3 py-1.5 text-sm font-medium text-[hsl(var(--success))]"
          data-testid="live-calls-count"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))]" />
          </span>
          {board.counts.all ?? 0} live
        </span>
        <Button
          variant="outline"
          className="rounded-lg"
          onClick={() => { setExtDraft(monitorSip); setExtOpen(true); }}
          data-testid="live-calls-monitor-extension"
        >
          {monitorSip ? <PencilSimple className="w-4 h-4 mr-1.5" weight="bold" /> : null}
          {monitorSip || "Monitoring Extension"}
        </Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex h-auto flex-wrap gap-1" data-testid="live-calls-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = board.counts[t.k] ?? 0;
            return (
              <TabsTrigger
                key={t.k}
                value={t.k}
                data-testid={`live-calls-tab-${t.k}`}
                className="gap-2"
              >
                <Icon className="w-4 h-4" weight="duotone" />
                {t.label}
                <span
                  className="tabular-nums rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-foreground"
                  data-testid={`live-calls-tab-count-${t.k}`}
                >
                  {count}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.k} value={t.k} className="mt-4 space-y-4">
            <div className="relative max-w-md">
              <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="live-calls-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search phone, agent, campaign, lead…"
                className="pl-9 rounded-lg"
              />
            </div>

            {loading ? (
              <div className="text-sm text-muted-foreground">Loading live calls…</div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={rows.length === 0 ? WaveSine : MagnifyingGlass}
                title={rows.length === 0 ? "No live calls right now" : "No matching live calls"}
                description={
                  rows.length === 0
                    ? emptyCopy[t.k]
                    : "No live call matches your search. Try a different term."
                }
                testid="live-calls-empty"
              />
            ) : t.k === "ai_bot" ? (
              <BotTable
                rows={filtered}
                now={now}
                busyKey={busyKey}
                monitorBusy={monitorBusy}
                monitorSip={monitorSip}
                onDisconnect={setConfirm}
                onMonitor={monitorCall}
              />
            ) : (
              <VoiceTable
                rows={filtered}
                now={now}
                showType={t.k === "agents"}
                busyKey={busyKey}
                monitorBusy={monitorBusy}
                monitorSip={monitorSip}
                onDisconnect={setConfirm}
                onMonitor={monitorCall}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={extOpen} onOpenChange={(v) => !extSaving && setExtOpen(v)}>
        <DialogContent className="max-w-md" data-testid="live-calls-monitor-dialog">
          <DialogHeader>
            <DialogTitle>Monitoring Extension</DialogTitle>
            <DialogDescription>
              Enter the SIP phone that should ring when you join or silently listen to a live call. It is stored for this customer until you change or remove it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="monitor-sip">SIP phone</Label>
            <Input
              id="monitor-sip"
              data-testid="live-calls-monitor-sip"
              value={extDraft}
              onChange={(e) => setExtDraft(e.target.value)}
              placeholder="e.g. supervisor1"
              autoComplete="off"
              onKeyDown={(e) => { if (e.key === "Enter") saveExtension(); }}
            />
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {monitorSip ? (
              <Button
                variant="outline"
                className="rounded-lg text-destructive"
                disabled={extSaving}
                onClick={deleteExtension}
                data-testid="live-calls-monitor-delete"
              >
                Remove
              </Button>
            ) : <span />}
            <Button
              className="rounded-lg"
              disabled={extSaving}
              onClick={saveExtension}
              data-testid="live-calls-monitor-save"
            >
              {extSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && !busyKey && setConfirm(null)}>
        <DialogContent data-testid="live-call-disconnect-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect this call?</DialogTitle>
            <DialogDescription>
              {confirm?.phone_number
                ? `This immediately hangs up ${confirm.phone_number}. The customer and any connected agent will be disconnected.`
                : "This immediately hangs up the live call. The customer and any connected agent will be disconnected."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-lg"
              onClick={() => setConfirm(null)}
              disabled={!!busyKey}
              data-testid="live-call-disconnect-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-lg"
              onClick={() => disconnectCall(confirm)}
              disabled={!!busyKey}
              data-testid="live-call-disconnect-confirm"
            >
              {busyKey ? "Disconnecting…" : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VoiceTable({ rows, now, showType, busyKey, monitorBusy, monitorSip, onDisconnect, onMonitor }) {
  return (
    <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-calls-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Phone</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Agent</TableHead>
            <TableHead>Campaign</TableHead>
            {showType ? <TableHead>Type</TableHead> : null}
            <TableHead>Status</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Lead ID</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={rowKey(r)}
              data-testid={`live-call-row-${rowKey(r)}`}
            >
              <TableCell className="font-medium tabular-nums" data-testid="live-call-phone">{nn(r.phone_number)}</TableCell>
              <TableCell data-testid="live-call-customer">{nn(r.customer_name)}</TableCell>
              <TableCell data-testid="live-call-agent">{nn(r.agent_name)}</TableCell>
              <TableCell data-testid="live-call-campaign">{nn(r.campaign_name || r.campaign_id)}</TableCell>
              {showType ? (
                <TableCell className="capitalize" data-testid="live-call-type">
                  {r.dialing_type ? dialingTypeLabel(r.dialing_type) : "—"}
                </TableCell>
              ) : null}
              <TableCell><StatusBadge row={r} /></TableCell>
              <TableCell className="tabular-nums text-[hsl(var(--success))]" data-testid="live-call-duration">
                {fmtDur(liveDuration(r.started_at, r.duration_sec, now))}
              </TableCell>
              <TableCell className="tabular-nums" data-testid="live-call-lead-id">{nn(r.lead_id)}</TableCell>
              <TableCell className="text-right">
                <RowActions
                  row={r}
                  busy={busyKey === rowKey(r)}
                  monitorBusy={monitorBusy}
                  monitorSip={monitorSip}
                  onDisconnect={onDisconnect}
                  onMonitor={onMonitor}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BotTable({ rows, now, busyKey, monitorBusy, monitorSip, onDisconnect, onMonitor }) {
  return (
    <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-calls-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Phone</TableHead>
            <TableHead>Caller ID</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Campaign</TableHead>
            <TableHead>Prefix</TableHead>
            <TableHead>Lead ID</TableHead>
            <TableHead>Call To</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>VICIdial IP</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={rowKey(r)} data-testid={`live-call-row-${rowKey(r)}`}>
              <TableCell className="font-medium tabular-nums" data-testid="live-call-phone">{nn(r.phone_number)}</TableCell>
              <TableCell className="tabular-nums" data-testid="live-call-callerid">{nn(r.callerid)}</TableCell>
              <TableCell data-testid="live-call-client">{nn(r.client_id)}</TableCell>
              <TableCell data-testid="live-call-customer">{nn(r.client)}</TableCell>
              <TableCell data-testid="live-call-campaign">{nn(r.campaign_name || r.campaign_id)}</TableCell>
              <TableCell className="font-mono tabular-nums" data-testid="live-call-prefix">{nn(r.campaign_prefix)}</TableCell>
              <TableCell className="tabular-nums" data-testid="live-call-lead-id">{nn(r.lead_id)}</TableCell>
              <TableCell><CallToBadge state={r.call_state} callTo={r.call_to} /></TableCell>
              <TableCell className="tabular-nums text-[hsl(var(--success))]" data-testid="live-call-duration">
                {fmtDur(liveDuration(r.started_at, r.duration_sec, now))}
              </TableCell>
              <TableCell className="tabular-nums" data-testid="live-call-vicidial-ip">{nn(r.vicidial_ip)}</TableCell>
              <TableCell className="text-right">
                <RowActions
                  row={r}
                  busy={busyKey === rowKey(r)}
                  monitorBusy={monitorBusy}
                  monitorSip={monitorSip}
                  onDisconnect={onDisconnect}
                  onMonitor={onMonitor}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RowActions({ row, busy, monitorBusy, monitorSip, onDisconnect, onMonitor }) {
  const key = rowKey(row);
  return (
    <TooltipProvider delayDuration={200}>
      <div className="inline-flex items-center justify-end gap-1.5">
        {monitorSip ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8 rounded-lg"
                  disabled={!!monitorBusy}
                  onClick={() => onMonitor(row, "join")}
                  aria-label="Join call"
                  data-testid={`live-calls-join-${key}`}
                >
                  <PhonePlus className="w-4 h-4" weight="bold" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Join call</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8 rounded-lg"
                  disabled={!!monitorBusy}
                  onClick={() => onMonitor(row, "silent")}
                  aria-label="Silent monitoring"
                  data-testid={`live-calls-silent-${key}`}
                >
                  <Ear className="w-4 h-4" weight="bold" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Silent monitoring</TooltipContent>
            </Tooltip>
          </>
        ) : null}
        {canDisconnect(row) ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            disabled={busy}
            onClick={() => onDisconnect(row)}
            data-testid="live-call-disconnect"
            title="Disconnect this call"
          >
            <PhoneDisconnect className="w-4 h-4" weight="bold" />
            {busy ? "Disconnecting…" : "Disconnect"}
          </Button>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
